import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
// @ts-ignore - react-qr-scanner doesn't have TypeScript definitions
import QrScanner from 'react-qr-scanner';
import { fetchApi } from '../../lib/api';
import { Button, Card, CardHeader, CardTitle, CardContent, Select } from '../../components/ui';

interface Session {
  id: string;
  title: string;
  startTime: string;
  endTime: string;
  room: {
    name: string;
    building: string;
  };
}

interface CheckInResponse {
  id: string;
  attendeeId: string;
  attendeeName: string;
  sessionId: string;
  sessionTitle: string;
  checkedInAt: string;
  message: string;
}

export default function QRScanner() {
  const [selectedSessionId, setSelectedSessionId] = useState<string>('');
  const [isScanning, setIsScanning] = useState(false);
  const [scanResult, setScanResult] = useState<{
    type: 'success' | 'error' | 'duplicate';
    message: string;
  } | null>(null);
  const [manualAttendeeId, setManualAttendeeId] = useState<string>('');
  const [lastScannedCode, setLastScannedCode] = useState<string>('');
  const [lastScanTime, setLastScanTime] = useState<number>(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [scanCount, setScanCount] = useState(0);
  
  const queryClient = useQueryClient();

  // Safety mechanism: Reset processing state if stuck for more than 10 seconds
  useEffect(() => {
    if (!isProcessing) return;
    
    const timeout = setTimeout(() => {
      console.warn('⚠️ Processing timeout - resetting state');
      setIsProcessing(false);
    }, 10000); // 10 seconds
    
    return () => clearTimeout(timeout);
  }, [isProcessing]);

  // Reset scan count when session changes
  useEffect(() => {
    setScanCount(0);
  }, [selectedSessionId]);

  // Fetch sessions for selection
  const { data: sessions, isLoading: sessionsLoading } = useQuery<Session[]>({
    queryKey: ['admin', 'sessions'],
    queryFn: () => fetchApi<Session[]>('/admin/sessions'),
  });

  // Check-in mutation
  const checkInMutation = useMutation({
    mutationFn: (data: { qrData: string; sessionId: string }) =>
      fetchApi<CheckInResponse>('/admin/checkins', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: (data) => {
      console.log('✅ Check-in successful:', data);
      setScanCount(prev => prev + 1);
      console.log(`📊 Total scans this session: ${scanCount + 1}`);
      
      setScanResult({
        type: 'success',
        message: `✓ ${data.attendeeName} checked in!`,
      });
      
      // Only invalidate check-ins for this specific session to reduce API calls
      queryClient.invalidateQueries({ queryKey: ['checkins', selectedSessionId] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'sessions'] });
      
      // Clear success message after 2 seconds
      setTimeout(() => {
        setScanResult(null);
      }, 2000);
      
      // Allow next scan immediately after success
      setIsProcessing(false);
    },
    onError: (error: Error) => {
      console.error('❌ Check-in failed:', error);
      
      const errorMessage = error.message || String(error);
      
      // Check for rate limit errors
      if (errorMessage.toLowerCase().includes('rate limit') || 
          errorMessage.toLowerCase().includes('too many requests')) {
        setScanResult({
          type: 'error',
          message: '⚠️ Rate limit reached - wait a moment',
        });
        // Longer timeout for rate limit errors
        setTimeout(() => {
          setScanResult(null);
          setIsProcessing(false);
        }, 5000);
        return;
      }
      
      // Check for duplicate check-in (case-insensitive)
      if (errorMessage.toLowerCase().includes('already checked in') || 
          errorMessage.toLowerCase().includes('duplicate')) {
        setScanResult({
          type: 'duplicate',
          message: '⚠️ Already checked in',
        });
      } else if (errorMessage.toLowerCase().includes('not found')) {
        setScanResult({
          type: 'error',
          message: '❌ Attendee or session not found',
        });
      } else if (errorMessage.toLowerCase().includes('invalid qr')) {
        setScanResult({
          type: 'error',
          message: '❌ Invalid QR code',
        });
      } else {
        setScanResult({
          type: 'error',
          message: `❌ ${errorMessage}`,
        });
      }
      
      // Clear error message after 2 seconds
      setTimeout(() => {
        setScanResult(null);
      }, 2000);
      
      // Allow next scan immediately after error
      setIsProcessing(false);
    },
  });

  // Handle QR code scan with improved continuous scanning and debouncing
  const handleScan = (data: any) => {
    // Ignore if no data, no session selected, or currently processing
    if (!data || !selectedSessionId || isProcessing) {
      return;
    }

    const scannedData = data?.text || data;
    const now = Date.now();
    
    // Prevent rapid duplicate scans of the SAME code within 2 seconds
    // This gives enough time for the API call to complete and show result
    // But allow different codes to be scanned immediately
    if (scannedData === lastScannedCode && (now - lastScanTime) < 2000) {
      console.log('⏭️ Skipping duplicate scan of same code (too soon)');
      return;
    }

    console.log('📷 QR Code detected:', scannedData);
    console.log('📷 Time since last scan:', now - lastScanTime, 'ms');
    
    // Mark as processing to prevent overlapping requests
    setIsProcessing(true);
    setLastScannedCode(scannedData);
    setLastScanTime(now);

    // Submit check-in
    checkInMutation.mutate({
      qrData: scannedData,
      sessionId: selectedSessionId,
    });
  };

  // Handle scan error
  const handleError = (error: any) => {
    console.error('📷 Scanner error:', error);
  };

  // Handle manual check-in
  const handleManualCheckIn = () => {
    if (!selectedSessionId || !manualAttendeeId.trim()) {
      setScanResult({
        type: 'error',
        message: 'Please select a session and enter an attendee ID',
      });
      return;
    }

    const qrData = `AIDAY:${manualAttendeeId.trim()}`;
    
    checkInMutation.mutate({
      qrData,
      sessionId: selectedSessionId,
    });

    setManualAttendeeId('');
  };

  // Format session display
  const formatSessionDisplay = (session: Session) => {
    const startTime = new Date(session.startTime).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
    return `${session.title} - ${startTime} (${session.room.building} ${session.room.name})`;
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>QR Code Scanner</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Session Selection */}
            <div>
              <Select
                label="Select Session"
                value={selectedSessionId}
                onChange={(e) => {
                  setSelectedSessionId(e.target.value);
                  setIsScanning(false);
                }}
                disabled={sessionsLoading || isScanning}
              >
                <option value="">-- Select a session --</option>
                {sessions?.map((session) => (
                  <option key={session.id} value={session.id}>
                    {formatSessionDisplay(session)}
                  </option>
                ))}
              </Select>
            </div>

            {/* Scanner Controls */}
            <div className="flex gap-3">
              {!isScanning ? (
                <Button
                  onClick={() => setIsScanning(true)}
                  disabled={!selectedSessionId || sessionsLoading}
                >
                  Start Scanning
                </Button>
              ) : (
                <Button onClick={() => setIsScanning(false)} variant="secondary">
                  Stop Scanning
                </Button>
              )}
            </div>

            {/* Scan Result */}
            {scanResult && (
              <div
                className={`p-4 rounded-md border ${
                  scanResult.type === 'success'
                    ? 'bg-green-50 border-green-200'
                    : scanResult.type === 'duplicate'
                    ? 'bg-yellow-50 border-yellow-200'
                    : 'bg-red-50 border-red-200'
                }`}
              >
                <p
                  className={`text-sm font-medium ${
                    scanResult.type === 'success'
                      ? 'text-green-800'
                      : scanResult.type === 'duplicate'
                      ? 'text-yellow-800'
                      : 'text-red-800'
                  }`}
                >
                  {scanResult.message}
                </p>
              </div>
            )}

            {/* Scanner Display */}
            <div className="relative">
              {isScanning && selectedSessionId ? (
                <div className="w-full max-w-md mx-auto">
                  <QrScanner
                    delay={300}
                    onError={handleError}
                    onScan={handleScan}
                    style={{ width: '100%' }}
                    constraints={{
                      video: { facingMode: 'environment' }
                    }}
                  />
                  <div className="mt-3 text-center">
                    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-md border ${
                      isProcessing 
                        ? 'bg-yellow-100 border-yellow-300' 
                        : 'bg-blue-100 border-blue-300'
                    }`}>
                      <div className={`w-2 h-2 rounded-full animate-pulse ${
                        isProcessing ? 'bg-yellow-600' : 'bg-blue-600'
                      }`}></div>
                      <span className={`text-sm font-medium ${
                        isProcessing ? 'text-yellow-900' : 'text-blue-900'
                      }`}>
                        {isProcessing ? 'Processing...' : 'Ready to scan'}
                      </span>
                    </div>
                    {scanCount > 0 && (
                      <p className="text-xs text-gray-600 mt-2">
                        ✓ {scanCount} check-in{scanCount !== 1 ? 's' : ''} completed
                      </p>
                    )}
                    <p className="text-xs text-gray-600 mt-1">
                      💡 Camera stays active - scan multiple QR codes continuously
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-64 bg-gray-100 rounded-md">
                  <p className="text-gray-500">
                    {selectedSessionId
                      ? 'Click "Start Scanning" to begin'
                      : 'Select a session to start scanning'}
                  </p>
                </div>
              )}
            </div>

            {/* Instructions */}
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <h4 className="text-sm font-semibold text-blue-900 mb-2">Instructions:</h4>
              <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                <li>Select the session you want to check attendees into</li>
                <li>Click "Start Scanning" to activate the camera</li>
                <li>Point the camera at each attendee's QR code</li>
                <li>The system will automatically record each check-in</li>
                <li>Camera stays active - scan multiple attendees continuously</li>
                <li>Duplicate scans are prevented automatically</li>
              </ol>
            </div>

            {/* Manual Check-in for Testing */}
            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
              <h4 className="text-sm font-semibold text-yellow-900 mb-3">Manual Check-in (Testing)</h4>
              <p className="text-sm text-yellow-800 mb-3">
                For testing: Enter an attendee ID directly to simulate a QR scan
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={manualAttendeeId}
                  onChange={(e) => setManualAttendeeId(e.target.value)}
                  placeholder="Paste attendee ID here"
                  className="flex-1 px-3 py-2 border border-yellow-300 rounded-md text-sm"
                  disabled={!selectedSessionId}
                />
                <Button
                  onClick={handleManualCheckIn}
                  disabled={!selectedSessionId || !manualAttendeeId.trim() || checkInMutation.isPending}
                  size="sm"
                >
                  Check In
                </Button>
              </div>
              <p className="text-xs text-yellow-700 mt-2">
                Tip: Go to Attendees page, click "QR Code" on any attendee, and copy their ID
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
